import { motion } from "framer-motion";
import { Check, X } from "lucide-react";

const traditionalItems = [
  "Shared leads sold to you and 5 other tradies",
  "Full of tyre-kickers and price shoppers",
  "You chase every enquiry manually",
  "No qualification — anyone counts as a lead",
  "No systems, no CRM, no follow-up automation",
  "Generic agencies that don't understand tradies",
];

const systemItems = [
  "Exclusive leads only for your business",
  "Qualified customers ready to book",
  "Automated follow-up handles replies + reminders",
  "Missed-call text-back saves lost jobs",
  "CRM pipelines tracking every lead to 'job won'",
  "A complete digital growth system built for trades",
];

const columnVariants = {
  hidden: { opacity: 0, y: 60 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.7,
      ease: [0.25, 0.46, 0.45, 0.94] as [number, number, number, number],
    },
  },
};

const listVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      delayChildren: 0.3,
      staggerChildren: 0.1,
    },
  },
};

const listItemVariants = {
  hidden: { opacity: 0, x: -20 },
  visible: {
    opacity: 1,
    x: 0,
    transition: {
      duration: 0.5,
      ease: [0.25, 0.46, 0.45, 0.94] as [number, number, number, number],
    },
  },
};

export function ComparisonSection() {
  return (
    <section className="section-padding bg-gradient-to-b from-background via-muted/30 to-background relative overflow-hidden" aria-labelledby="comparison-heading">
      <div className="absolute inset-0 grid-pattern opacity-30" />
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[400px] sm:w-[600px] h-[200px] sm:h-[300px] bg-primary/[0.03] rounded-full blur-[80px] sm:blur-[100px]" />
      
      <div className="container-wide relative">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.5, ease: "easeOut" }}
          className="text-center mb-6 sm:mb-8"
        >
          <div className="section-badge mb-3 sm:mb-4 text-xs">
            <span>The Difference</span>
          </div>
          <h2 id="comparison-heading" className="heading-section text-foreground max-w-3xl mx-auto">
            Traditional Lead Gen vs. Our Trade Marketing System
          </h2>
          <p className="text-muted-foreground text-base mt-3 max-w-xl mx-auto">
            See why Australian trade and construction businesses choose our digital growth system over generic lead generation services.
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-0 max-w-5xl mx-auto rounded-2xl overflow-hidden shadow-xl">
          <motion.div
            variants={columnVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-80px" }}
            className="relative p-5 sm:p-6 lg:p-8 bg-gradient-to-br from-[#FDE8E8] to-[#FCDADA]"
          >
            <div className="relative">
              <div className="flex items-center gap-3 mb-5 sm:mb-6">
                <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-xl bg-[#E24A4A] flex items-center justify-center shadow-lg shadow-[#E24A4A]/30" aria-hidden="true">
                  <X className="w-4 h-4 sm:w-5 sm:h-5 text-white" strokeWidth={3} />
                </div>
                <h3 className="text-base sm:text-lg lg:text-xl font-bold text-[#C93636]">Traditional Lead Gen</h3>
              </div>
              <motion.ul variants={listVariants} initial="hidden" whileInView="visible" viewport={{ once: true }} className="space-y-2.5" aria-label="Problems with traditional lead generation">
                {traditionalItems.map((item, index) => (
                  <motion.li key={index} variants={listItemVariants} className="flex items-start gap-2.5">
                    <div className="w-5 h-5 rounded-full bg-[#E24A4A]/20 flex items-center justify-center flex-shrink-0 mt-0.5" aria-hidden="true">
                      <X className="w-2.5 h-2.5 text-[#E24A4A]" strokeWidth={3} />
                    </div>
                    <span className="text-[#5A3030] leading-[1.65] text-[0.9375rem] sm:text-base font-medium">{item}</span>
                  </motion.li>
                ))}
              </motion.ul>
            </div>
          </motion.div>

          <motion.div
            variants={columnVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-80px" }}
            transition={{ delay: 0.1 }}
            className="relative p-5 sm:p-6 lg:p-8 bg-gradient-to-br from-[#EAF6E5] to-[#DCF0D4]"
          >
            <div className="relative">
              <div className="flex items-center gap-3 mb-5 sm:mb-6">
                <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-xl bg-[#4F9B29] flex items-center justify-center shadow-lg shadow-[#4F9B29]/30" aria-hidden="true">
                  <Check className="w-4 h-4 sm:w-5 sm:h-5 text-white" strokeWidth={3} />
                </div>
                <h3 className="text-base sm:text-lg lg:text-xl font-bold text-[#3D7A1F]">Our Tradie Marketing System</h3>
              </div>
              <motion.ul variants={listVariants} initial="hidden" whileInView="visible" viewport={{ once: true }} className="space-y-2.5" aria-label="Benefits of our tradie marketing system">
                {systemItems.map((item, index) => (
                  <motion.li key={index} variants={listItemVariants} className="flex items-start gap-2.5">
                    <div className="w-5 h-5 rounded-full bg-[#4F9B29]/20 flex items-center justify-center flex-shrink-0 mt-0.5" aria-hidden="true">
                      <Check className="w-2.5 h-2.5 text-[#4F9B29]" strokeWidth={3} />
                    </div>
                    <span className="text-[#2A4520] leading-[1.65] text-[0.9375rem] sm:text-base font-medium">{item}</span>
                  </motion.li>
                ))}
              </motion.ul>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
